import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, CalendarRange } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getMonday(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  return monday.toISOString().split('T')[0];
}

function getSunday(mondayStr) {
  const d = new Date(mondayStr + 'T00:00:00');
  d.setDate(d.getDate() + 6);
  return d.toISOString().split('T')[0];
}

function formatWeekRange(mondayStr) {
  const monday = new Date(mondayStr + 'T00:00:00');
  const sunday = new Date(mondayStr + 'T00:00:00');
  sunday.setDate(sunday.getDate() + 6);
  const opts = { month: 'short', day: 'numeric', year: 'numeric' };
  return `${monday.toLocaleDateString('en-US', opts)} - ${sunday.toLocaleDateString('en-US', opts)}`;
}

export default function TimesheetCreate() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [timesheetType, setTimesheetType] = useState('DAILY');
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    shift: 'Day'
  });
  const [weekDate, setWeekDate] = useState(() => {
    return getMonday(new Date().toISOString().split('T')[0]);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        shift: formData.shift,
        type: timesheetType,
      };

      if (timesheetType === 'WEEKLY') {
        const monday = getMonday(weekDate);
        payload.date = monday;
        payload.weekEndingDate = getSunday(monday);
      } else {
        payload.date = formData.date;
      }

      const response = await axios.post(`${API}/timesheets`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${timesheetType === 'WEEKLY' ? 'Weekly' : 'Daily'} timesheet created`);
      navigate(`/timesheets/${response.data.id}`);
    } catch (error) {
      console.error('Failed to create timesheet:', error);
      toast.error(error.response?.data?.detail || 'Failed to create timesheet');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='p-4 md:p-8' data-testid='timesheet-create-page'>
      <Button
        onClick={() => navigate('/timesheets')}
        variant='ghost'
        className='mb-4 text-slate-600 hover:text-[#1A3668] rounded-sm'
      >
        <ArrowLeft className='w-4 h-4 mr-2' />
        Back to Timesheets
      </Button>

      <Card className='max-w-2xl mx-auto border-slate-200 shadow-sm rounded-sm'>
        <CardHeader className='p-4 md:p-6'>
          <CardTitle className='text-xl md:text-3xl font-bold text-slate-900'>Create New Timesheet</CardTitle>
        </CardHeader>
        <CardContent className='p-4 md:p-6 pt-0 md:pt-0'>
          <form onSubmit={handleSubmit} className='space-y-6'>
            {/* Timesheet Type Toggle */}
            <div>
              <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                Timesheet Type *
              </Label>
              <div className='mt-2 flex gap-2'>
                <Button
                  type='button'
                  data-testid='type-daily-btn'
                  onClick={() => setTimesheetType('DAILY')}
                  variant={timesheetType === 'DAILY' ? 'default' : 'outline'}
                  className={`flex-1 rounded-sm py-3 font-bold ${
                    timesheetType === 'DAILY'
                      ? 'bg-[#1A3668] hover:bg-[#142A52] text-white'
                      : 'border-2 border-slate-200 text-slate-600 hover:border-[#1A3668]'
                  }`}
                >
                  <Calendar className='w-4 h-4 mr-2' />
                  Daily
                </Button>
                <Button
                  type='button'
                  data-testid='type-weekly-btn'
                  onClick={() => setTimesheetType('WEEKLY')}
                  variant={timesheetType === 'WEEKLY' ? 'default' : 'outline'}
                  className={`flex-1 rounded-sm py-3 font-bold ${
                    timesheetType === 'WEEKLY'
                      ? 'bg-[#1A3668] hover:bg-[#142A52] text-white'
                      : 'border-2 border-slate-200 text-slate-600 hover:border-[#1A3668]'
                  }`}
                >
                  <CalendarRange className='w-4 h-4 mr-2' />
                  Weekly
                </Button>
              </div>
            </div>

            {/* Date Selection */}
            {timesheetType === 'DAILY' ? (
              <div>
                <Label htmlFor='date' className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                  Date *
                </Label>
                <Input
                  id='date'
                  data-testid='timesheet-date-input'
                  type='date'
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  className='mt-2 rounded-sm border-2 py-3 md:py-2 text-base md:text-sm'
                />
              </div>
            ) : (
              <div>
                <Label htmlFor='weekDate' className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                  Week Starting Date *
                </Label>
                <Input
                  id='weekDate'
                  data-testid='timesheet-week-date-input'
                  type='date'
                  value={weekDate}
                  onChange={(e) => setWeekDate(getMonday(e.target.value))}
                  required
                  className='mt-2 rounded-sm border-2 py-3 md:py-2 text-base md:text-sm'
                />
                <p className='mt-2 text-sm text-slate-500 font-medium'>
                  Week: <span className='text-[#1A3668] font-bold'>{formatWeekRange(weekDate)}</span>
                </p>
              </div>
            )}

            <div>
              <Label htmlFor='shift' className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                Shift *
              </Label>
              <Select
                value={formData.shift}
                onValueChange={(value) => setFormData({ ...formData, shift: value })}
              >
                <SelectTrigger data-testid='shift-select' className='mt-2 rounded-sm border-2 py-3 md:py-2 text-base md:text-sm'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='Day'>Day</SelectItem>
                  <SelectItem value='Night'>Night</SelectItem>
                  <SelectItem value='Swing'>Swing</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className='flex flex-col sm:flex-row gap-3 pt-4'>
              <Button
                type='button'
                variant='outline'
                onClick={() => navigate('/timesheets')}
                className='border-2 border-slate-200 rounded-sm py-3 md:py-2 order-2 sm:order-1'
              >
                Cancel
              </Button>
              <Button
                type='submit'
                data-testid='create-timesheet-submit-button'
                disabled={submitting}
                className='bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm py-4 md:py-2 text-base active:scale-[0.98] order-1 sm:order-2 flex-1 sm:flex-none'
              >
                {submitting ? 'Creating...' : `Create ${timesheetType === 'WEEKLY' ? 'Weekly' : 'Daily'} Timesheet`}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
